import ScrollReveal from '@/components/ScrollReveal';
import { Link } from 'react-router-dom';
import { Star, MapPin, Waves, Sun, Anchor, ChevronDown, ArrowRight, Bed, Eye, Droplets, Wifi, Wind, UtensilsCrossed, Quote } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useState } from 'react';
import Lightbox from '@/components/Lightbox';

// Original property images
import originalHeraView from '@/assets/original-hera-view.jpg';
import originalHeraPanorama from '@/assets/original-hera-panorama.jpg';
import originalHeraTerrace from '@/assets/original-hera-terrace.jpg';
import originalHeraLiving from '@/assets/original-hera-living.jpg';
import originalHeraBedroom from '@/assets/original-hera-bedroom.jpg';
import originalZeusPool from '@/assets/original-zeus-pool.jpg';
import originalZeusCourtyard from '@/assets/original-zeus-courtyard.jpg';
import originalZeusInterior from '@/assets/original-zeus-interior.jpg';
import originalZeusSunset from '@/assets/original-zeus-sunset.jpg';
import originalZeusLounge from '@/assets/original-zeus-lounge.jpg';
import originalZeusDining from '@/assets/original-zeus-dining.jpg';
import originalZeusBedroom from '@/assets/original-zeus-bedroom.jpg';
import originalZeusView from '@/assets/original-zeus-view.jpg';
import originalZeusKitchen from '@/assets/original-zeus-kitchen.jpg';
import originalZeusBathroom from '@/assets/original-zeus-bathroom.jpg';
import sivotaAerial from '@/assets/sivota-aerial.jpg';
import heraFeature from '@/assets/hera-feature.jpg';
import paxosImg from '@/assets/paxos.jpg';
import antipaxosImg from '@/assets/antipaxos.jpg';
import corfuImg from '@/assets/corfu.jpg';
import balconyViewSivota from '@/assets/balcony-view-sivota.jpg';

export default function Home() {
  const { t, language } = useLanguage();
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  const reviews = [
    { name: 'Maria K.', text: t('featuredQuote').replace(/[""]/g, ''), rating: 5, location: 'Athens, Greece' },
    { name: 'James R.', text: 'The apartments are very spacious and the view is simply stunning with the endless blue sea and Corfu island in the distance.', rating: 5, location: 'London, UK' },
    { name: 'Sophie L.', text: 'Pure luxury in the most beautiful setting. The apartments are immaculate and the location is paradise.', rating: 5, location: 'Paris, France' },
    { name: 'Hans W.', text: 'Dimitris is the perfect host. The stone-built apartments are a work of art and the sunsets are unforgettable.', rating: 5, location: 'Munich, Germany' },
  ];

  const destinations = [
    { name: 'Paxos', desc: t('paxosDesc'), img: paxosImg, time: t('paxosTime') },
    { name: 'Antipaxos', desc: t('antipaxosDesc'), img: antipaxosImg, time: t('antipaxosTime') },
    { name: 'Corfu', desc: t('corfuDesc'), img: corfuImg, time: t('corfuTime') },
  ];

  const galleryImages = [
    balconyViewSivota, originalZeusPool, originalHeraTerrace, originalZeusSunset,
    originalZeusCourtyard, heraFeature, originalHeraView, originalZeusLounge,
    originalHeraPanorama
  ];

  const handleGalleryKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setLightboxIndex(index);
    }
  };

  return (
    <div className="min-h-screen">
      {/* ═══ HERO ═══ */}
      <section className="relative h-[100svh] flex items-center overflow-hidden">
        <img src={originalZeusSunset} alt="Golden sunset over the Ionian Sea from Sivota Royal" className="absolute inset-0 w-full h-full object-cover hero-image-bright" width={1920} height={1080} fetchPriority="high" style={{ animation: 'heroZoom 25s ease-out forwards' }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/15" />
        <div className="relative z-10 section-padding mt-16 sm:mt-0 w-full max-w-4xl">
          <p className="text-white/80 tracking-[0.4em] uppercase text-xs font-sans mb-5 animate-reveal drop-shadow-lg">
            {t('sivotaIonianGreece')}
          </p>
          <h1 className={`font-serif font-light text-white mb-5 animate-reveal leading-[1.05] drop-shadow-xl [text-shadow:_0_2px_20px_rgba(0,0,0,0.4)] ${
            language === 'el' ? 'text-3xl md:text-5xl lg:text-6xl' : 'text-5xl md:text-7xl lg:text-[5.5rem]'
          }`}>
            {t('whereSeaMeetsSky')}
          </h1>
          <p className="font-sans text-white/90 text-lg md:text-xl max-w-lg animate-reveal-delay drop-shadow-lg [text-shadow:_0_1px_10px_rgba(0,0,0,0.3)] mb-10">
            {t('heroLongDesc')}
          </p>
          <div className="flex flex-row gap-3 sm:gap-4 animate-reveal-delay-2">
            <Link to="/booking"
              className="hidden sm:block bg-accent text-accent-foreground px-10 py-4 text-sm tracking-[0.15em] uppercase hover:bg-accent/90 transition-colors text-center">
              {t('checkAvailability')}
            </Link>
            <Link to="/apartments" className="border border-white/50 text-white px-8 sm:px-10 py-3.5 sm:py-4 text-sm tracking-[0.15em] uppercase hover:bg-white/10 transition-colors text-center">
              {t('viewApartments')}
            </Link>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/50 scroll-indicator hidden sm:block" aria-hidden="true">
          <ChevronDown size={28} />
        </div>
      </section>

      {/* ═══ TRUST BAR ═══ */}
      <section className="bg-secondary py-6">
        <div className="section-padding flex flex-wrap justify-center gap-8 lg:gap-16 items-center text-center">
          <div className="flex items-center gap-2">
            <div className="flex gap-0.5" aria-label="5 stars rating">
              {[1,2,3,4,5].map(i => <Star key={i} size={14} className="fill-accent text-accent" aria-hidden="true" />)}
            </div>
            <span className="text-sm text-muted-foreground font-sans">{t('onBookingCom')}</span>
          </div>
          <div className="hidden sm:block w-px h-5 bg-border" aria-hidden="true" />
          <span className="text-sm text-muted-foreground font-sans">{t('hilltopViews')}</span>
          <div className="hidden sm:block w-px h-5 bg-border" aria-hidden="true" />
          <span className="text-sm text-muted-foreground font-sans">{t('twoMinBeach')}</span>
          <div className="hidden sm:block w-px h-5 bg-border" aria-hidden="true" />
          <span className="text-sm text-muted-foreground font-sans">{t('privatePool')}</span>
        </div>
      </section>

      {/* ═══ INTRO ═══ */}
      <section className="section-padding py-24 lg:py-36">
        <div className="max-w-4xl mx-auto text-center">
          <ScrollReveal>
            <p className="text-luxury mb-6">{t('welcome')}</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-foreground mb-8 leading-tight">
              {t('whereMedComesAlive')}
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed max-w-2xl mx-auto">
              {t('indexIntroDesc')}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* ═══ HERA APARTMENT ═══ */}
      <section className="section-padding pb-6 lg:pb-10">
        <ScrollReveal>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 items-stretch rounded-sm overflow-hidden shadow-lg">
            <div className="image-zoom aspect-[4/3] lg:aspect-auto lg:min-h-[580px]">
              <img src={heraFeature} alt="Hera Apartment — balcony dining with Ionian views" className="w-full h-full object-cover hero-image-bright" loading="lazy" width={800} height={600} />
            </div>
            <div className="bg-card flex flex-col justify-center p-10 lg:p-16 xl:p-20">
              <p className="text-luxury mb-3 text-accent">{t('theApartments')}</p>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-foreground mb-5">Hera</h2>
              <div className="w-12 h-px bg-golden mb-6" />
              <p className="text-muted-foreground leading-relaxed mb-8 text-base lg:text-lg">
                {t('heraPanoramicDesc')}
              </p>
              <div className="grid grid-cols-3 gap-x-6 gap-y-3 mb-10">
                {[
                  { icon: Bed, label: t('twoBeds') },
                  { icon: Eye, label: t('seaViews') },
                  { icon: Wifi, label: t('wiFi') },
                  { icon: Wind, label: t('ac') },
                  { icon: UtensilsCrossed, label: t('kitchen') },
                  { icon: Waves, label: t('terrace') },
                ].map((a, i) => (
                  <div key={i} className="flex items-center gap-2.5 text-muted-foreground text-sm">
                    <a.icon size={16} className="text-golden shrink-0" aria-hidden="true" />
                    <span>{a.label}</span>
                  </div>
                ))}
              </div>
              <Link to="/apartments/hera" className="group inline-flex items-center gap-3 bg-accent text-accent-foreground px-8 py-3.5 text-sm tracking-[0.15em] uppercase hover:bg-accent/90 transition-all duration-300 self-start">
                {t('exploreHera')} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* ═══ ZEUS APARTMENT ═══ */}
      <section className="section-padding pb-24 lg:pb-36">
        <ScrollReveal>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 items-stretch rounded-sm overflow-hidden shadow-lg">
            <div className="bg-card flex flex-col justify-center p-10 lg:p-16 xl:p-20 order-2 lg:order-1">
              <p className="text-luxury mb-3 text-accent">{t('theApartments')}</p>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-foreground mb-5">Zeus</h2>
              <div className="w-12 h-px bg-golden mb-6" />
              <p className="text-muted-foreground leading-relaxed mb-8 text-base lg:text-lg">
                {t('zeusPoolDesc')}
              </p>
              <div className="grid grid-cols-3 gap-x-6 gap-y-3 mb-10">
                {[
                  { icon: Bed, label: t('twoBeds') },
                  { icon: Droplets, label: t('pool') },
                  { icon: Eye, label: t('views') },
                  { icon: Wifi, label: t('wiFi') },
                  { icon: Wind, label: t('ac') },
                  { icon: UtensilsCrossed, label: t('kitchen') },
                ].map((a, i) => (
                  <div key={i} className="flex items-center gap-2.5 text-muted-foreground text-sm">
                    <a.icon size={16} className="text-golden shrink-0" aria-hidden="true" />
                    <span>{a.label}</span>
                  </div>
                ))}
              </div>
              <Link to="/apartments/zeus" className="group inline-flex items-center gap-3 bg-accent text-accent-foreground px-8 py-3.5 text-sm tracking-[0.15em] uppercase hover:bg-accent/90 transition-all duration-300 self-start">
                {t('exploreZeus')} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </Link>
            </div>
            <div className="image-zoom aspect-[4/3] lg:aspect-auto lg:min-h-[580px] order-1 lg:order-2">
              <img src={originalZeusPool} alt="Zeus Apartment — private pool and courtyard" className="w-full h-full object-cover hero-image-bright" loading="lazy" width={800} height={600} />
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* ═══ CINEMATIC PULL QUOTE ═══ */}
      <section className="relative overflow-hidden">
        <ScrollReveal>
          <div className="image-zoom aspect-[21/9]">
            <img src={balconyViewSivota} alt="Balcony breakfast with panoramic Sivota village and Ionian Sea view" className="w-full h-full object-cover hero-image-bright" loading="lazy" width={1920} height={823} />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/25 to-transparent flex items-center justify-center">
            <div className="text-center max-w-4xl px-6">
              <Quote size={32} className="text-white/30 mx-auto mb-4" aria-hidden="true" />
              <p className="font-serif text-4xl md:text-5xl lg:text-7xl font-light text-white drop-shadow-lg leading-tight">
                {t('bestViewsQuote')}
              </p>
              <p className="text-white/70 mt-6 text-sm tracking-[0.3em] uppercase">{t('ourGuests')}</p>
            </div>
          </div>
        </ScrollReveal>
      </section>

      {/* ═══ EXPERIENCE HIGHLIGHTS ═══ */}
      <section className="section-padding py-24 lg:py-36">
        <ScrollReveal>
          <div className="text-center mb-16">
            <p className="text-luxury mb-4">{t('theExperience')}</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-foreground">{t('medLivingFinest')}</h2>
          </div>
        </ScrollReveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6 max-w-5xl mx-auto">
          {[
            { icon: Sun, title: t('sunDrenchedTerraces'), desc: t('sunDrenchedDesc') },
            { icon: Waves, title: t('endlessBlueViews'), desc: t('endlessBlueDesc') },
            { icon: MapPin, title: t('crystalClearBeaches'), desc: t('crystalClearDesc') },
            { icon: Anchor, title: t('authenticGreekLife'), desc: t('authenticGreekDesc') },
          ].map((item, i) => (
            <ScrollReveal key={i} delay={i * 0.1}>
              <div className="text-center group">
                <div className="w-14 h-14 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center mx-auto mb-5 group-hover:bg-accent/20 transition-colors duration-300">
                  <item.icon size={22} className="text-accent" aria-hidden="true" />
                </div>
                <h3 className="font-serif text-xl text-foreground mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* ═══ CURATED GALLERY ═══ */}
      <section className="py-20 lg:py-32 bg-secondary">
        <ScrollReveal>
          <div className="text-center mb-14 section-padding">
            <p className="text-luxury mb-4">{t('gallery')}</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-foreground">{t('sunSeaStone')}</h2>
          </div>
        </ScrollReveal>
        <ScrollReveal delay={0.1}>
          <div className="section-padding">
            <div className="grid grid-cols-12 gap-2 md:gap-3 max-w-7xl mx-auto">
              {[
                { span: 'col-span-8', aspect: 'aspect-[16/9]', alt: 'Balcony breakfast view', idx: 0 },
                { span: 'col-span-4 row-span-2', aspect: '', alt: 'Private pool', idx: 1 },
                { span: 'col-span-4', aspect: 'aspect-square', alt: 'Sun-drenched terrace', idx: 2 },
                { span: 'col-span-4', aspect: 'aspect-square', alt: 'Golden sunset', idx: 3 },
                { span: 'col-span-4', aspect: 'aspect-[4/3]', alt: 'Stone courtyard', idx: 4 },
                { span: 'col-span-4', aspect: 'aspect-[4/3]', alt: 'Dining with views', idx: 5 },
                { span: 'col-span-4', aspect: 'aspect-[4/3]', alt: 'Panoramic sea view', idx: 6 },
                { span: 'col-span-7', aspect: 'aspect-[16/9]', alt: 'Lounge area', idx: 7 },
                { span: 'col-span-5', aspect: 'aspect-[16/9]', alt: 'Panoramic terrace', idx: 8 },
              ].map(item => (
                <div
                  key={item.idx}
                  className={`${item.span} image-zoom cursor-pointer rounded-sm overflow-hidden ${item.aspect}`}
                  onClick={() => setLightboxIndex(item.idx)}
                  onKeyDown={(e) => handleGalleryKeyDown(e, item.idx)}
                  role="button"
                  tabIndex={0}
                  aria-label={`View ${item.alt} full size`}
                >
                  <img src={galleryImages[item.idx]} alt={item.alt} className="w-full h-full object-cover hero-image-bright" loading="lazy" width={800} height={600} />
                </div>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </section>

      {lightboxIndex >= 0 && (
        <Lightbox images={galleryImages} initialIndex={lightboxIndex} onClose={() => setLightboxIndex(-1)} />
      )}

      {/* ═══ DESTINATIONS ═══ */}
      <section className="section-padding py-24 lg:py-36">
        <ScrollReveal>
          <div className="text-center mb-14">
            <p className="text-luxury mb-4">{t('discover')}</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-foreground mb-4">{t('islandHoppingFromSivota')}</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">{t('islandHoppingDesc')}</p>
          </div>
        </ScrollReveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
          {destinations.map((d, i) => (
            <ScrollReveal key={i} delay={i * 0.12}>
              <Link to="/destinations" className="group block relative rounded-sm overflow-hidden aspect-[3/4]">
                <img src={d.img} alt={d.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" width={600} height={800} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
                  <p className="text-white/80 text-xs tracking-[0.2em] uppercase mb-2">{d.time}</p>
                  <h3 className="font-serif text-3xl lg:text-4xl text-white mb-1">{d.name}</h3>
                  <p className="text-white/80 text-sm">{d.desc}</p>
                </div>
                <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-hidden="true">
                  <ArrowRight size={16} className="text-white" />
                </div>
              </Link>
            </ScrollReveal>
          ))}
        </div>
        <ScrollReveal delay={0.3}>
          <div className="text-center mt-10">
            <Link to="/destinations" className="group inline-flex items-center gap-3 border border-foreground/30 px-8 py-3.5 text-sm tracking-[0.15em] uppercase hover:bg-foreground hover:text-background transition-all duration-300">
              {t('exploreDestinations')}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" />
            </Link>
          </div>
        </ScrollReveal>
      </section>

      {/* ═══ REVIEWS ═══ */}
      <section className="bg-secondary py-24 lg:py-36">
        <div className="section-padding">
          <ScrollReveal>
            <div className="text-center mb-16">
              <p className="text-luxury mb-4">{t('guestExperiences')}</p>
              <h2 className="font-serif text-4xl md:text-5xl font-light text-foreground">{t('whatGuestsSay')}</h2>
            </div>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {reviews.map((r, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div className="bg-background p-8 rounded-sm border border-border/60 h-full flex flex-col">
                  <div className="flex gap-1 mb-5" aria-label={`${r.rating} out of 5 stars`}>
                    {Array.from({ length: r.rating }).map((_, j) => (
                      <Star key={j} size={14} className="fill-golden text-golden" aria-hidden="true" />
                    ))}
                  </div>
                  <p className="text-foreground/80 leading-relaxed mb-6 text-sm flex-1">"{r.text}"</p>
                  <div>
                    <p className="font-serif text-lg text-foreground">{r.name}</p>
                    <p className="text-muted-foreground text-xs mt-0.5">{r.location}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
          <ScrollReveal delay={0.3}>
            <div className="text-center mt-12">
              <Link to="/reviews" className="group inline-flex items-center gap-3 text-sm tracking-[0.15em] uppercase text-foreground hover:text-accent transition-colors">
                {t('readAllReviews')} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ═══ MAP ═══ */}
      <section>
        <ScrollReveal>
          <div className="text-center section-padding py-16">
            <p className="text-luxury mb-4">{t('location')}</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-foreground mb-4">{t('findUs')}</h2>
            <p className="text-muted-foreground">{t('sivotaGreece')}</p>
          </div>
          <div className="w-full h-[400px] lg:h-[500px]">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3082.9315172226143!2d20.252206576273593!3d39.403055371620646!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x135c81fa0f9ed3a9%3A0xa31f6849d913986b!2sSivota%20Royal!5e0!3m2!1sen!2sus!4v1719951580877!5m2!1sen!2sus"
              width="100%" height="100%" style={{ border: 0, filter: 'saturate(0.8) contrast(1.1)' }} allowFullScreen loading="lazy" title="Sivota Royal Location"
            />
          </div>
        </ScrollReveal>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="relative py-32 lg:py-44">
        <img src={originalZeusSunset} alt="" className="absolute inset-0 w-full h-full object-cover hero-image-bright" loading="lazy" width={1920} height={1080} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-black/10" />
        <div className="relative z-10 text-center section-padding">
          <ScrollReveal>
            <p className="text-white/80 tracking-[0.3em] uppercase text-xs font-sans mb-6">{t('startPlanning')}</p>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl font-light text-white mb-6 drop-shadow-lg">
              {t('yourMedEscape')}
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-lg mx-auto drop-shadow-md">
              {t('bookDirectlyBestRates')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/booking"
                className="bg-accent text-accent-foreground px-10 py-4 text-sm tracking-[0.15em] uppercase hover:bg-accent/90 transition-colors">
                {t('checkAvailability')}
              </Link>
              <Link to="/booking" className="border border-white/40 text-white px-10 py-4 text-sm tracking-[0.15em] uppercase hover:bg-white/10 transition-colors">
                {t('sendEnquiry')}
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
